import * as React from 'react';
import { WizardNavigationStatus, IWizardState, IWizardStep, wizardReducer } from './reducer';
import { EventManager } from '../../utils/event-manager';

export interface IAbstractWizardOptions {
    /**
     * default step to activate when the wizard is initialized.
     * this is not a controlled property, so changing the value once the wizard is intialized would have no effect.
     */
    defaultActiveStep?: string;
}

export interface IWizardStepInfo {
    id: string;
}

export interface IWizardNavigationHookArgs {
    from?: string;
    to: string;
    params?: any;
}

export type WizardNavigationHook = (args: IWizardNavigationHookArgs) => Promise<boolean>;

export interface IWizardNavigationArgs {
    params?: any;
    beforeNavigate?: WizardNavigationHook;
}

export interface IWizardActions {
    navigate(stepId: string, args?: IWizardNavigationArgs): Promise<boolean>;
    addStep(step: IWizardStepInfo): void;
    removeStep(id: string): void;
    saveStepData(id: string, data: any): void;
    clearAllStepsData(): void;
}

export interface IWizardStateSelectors {
    nextStepId(stepId: string): string;
    previousStepId(stepId: string): string;
    hasStep(stepId: string): boolean;
    isActive(stepId: string): boolean;
    isFirst(stepId: string): boolean;
    isLast(stepId: string): boolean;
    isBetween(stepId: string): boolean;
    isStepInRange(stepId: string): boolean;
    getStepParams(stepId: string): any;
    getStepData(stepId: string): any;
    getStepsData(): { [key: string]: any; };
    hasVisited(stepId: string): boolean;
}

export interface IWizardDerivedState {
    currentStep: string;
    firstStep: string;
    lastStep: string;
    stepsLength: number;
    steps: string[];
    stepsState: { [key: string]: IWizardStep };
    stepsData: { [key: string]: any };
    navigationStatus: WizardNavigationStatus;
}

type WizardEventHandlers = {
    activated: { from: string; currentStep: string; };
    deactivating: { from: string; to: string; };
}

export type WizardEventHandler = <Key extends keyof WizardEventHandlers>(name: Key, handler: (args: WizardEventHandlers[Key]) => void) => () => void;

export interface IWizardInstance {
    state: IWizardDerivedState;
    actions: IWizardActions;
    selectors: IWizardStateSelectors;
    eventManager: EventManager;
    on: WizardEventHandler;
}

const DEFAULT_WIZARD_STATE: IWizardState = {
    currentStepId: '', stepIds: [], steps: {},
    stepsData: {},
    navigation: {
        status: 'idle'
    }
};

const noopNavHook = () => Promise.resolve(true);

export const useWizard = ({ defaultActiveStep }: IAbstractWizardOptions = {}) => {

    const eventManager = React.useMemo(() => new EventManager(), []);
    const [{ currentStepId, stepIds, navigation, stepsData, steps }, dispatch] = React.useReducer(wizardReducer, {
        ...DEFAULT_WIZARD_STATE, currentStepId: defaultActiveStep ?? ''
    });
    const lastActiveStepId = React.useRef<string>(currentStepId || '');
    const currentDerivedState = React.useRef<IWizardDerivedState>();

    const addStep = React.useCallback(({ id }: IWizardStepInfo) => {
        dispatch({ type: 'add', payload: { id } });
    }, []);

    const removeStep = React.useCallback((stepId: string) => {
        dispatch({ type: 'remove', payload: stepId });
        eventManager.emit('removed', { stepId });
    }, [eventManager]);

    const saveStepData = React.useCallback((stepId: string, data: any) => {
        dispatch({ type: 'save-step-data', payload: { stepId, data } });
    }, []);

    const clearAllStepsData = React.useCallback(() => {
        dispatch({ type: 'clear-all-steps-data' });
    }, []);

    const onStepDeactivating = React.useCallback((args: { from: string; to: string; }) => {
        eventManager.emit('deactivating', args);
        eventManager.emit(`#${args.from}:deactivating`, args);
    }, [eventManager]);

    const onStepActivated = React.useCallback((args: { from: string; currentStep: string; }) => {
        eventManager.emit('activated', args);
        // TODO: find a better way to emit activated event
        setTimeout(() => eventManager.emit(`#${args.currentStep}:activated`, args));
    }, [eventManager]);

    const eventsProxy = React.useMemo<WizardEventHandler>(() => (name, handler) => {
        eventManager.on(name, handler);
        return () => eventManager.off(name, handler);
    }, [eventManager]);

    const navigate = React.useCallback((nextStep: string, args?: IWizardNavigationArgs) => {
        dispatch({ type: 'start-navigate', payload: { to: nextStep, params: args?.params } });

        const currentStep = currentDerivedState.current?.currentStep;
        if (currentStep) {
            onStepDeactivating({ from: currentStep, to: nextStep });
        }

        return (args?.beforeNavigate ?? noopNavHook)({ to: nextStep, from: currentStep, params: args?.params })
            .then(canContinue => {
                if (canContinue) {
                    dispatch({ type: 'end-navigate', payload: { to: nextStep } });
                    // onStepActivated({ from: currentStep || '', currentStep: nextStep });
                    return canContinue;
                }
                dispatch({ type: 'cancel-navigate', payload: { to: nextStep } });
                return false;
            }).catch(() => {
                dispatch({ type: 'cancel-navigate', payload: { to: nextStep } });
                return false;
            });
    }, [onStepDeactivating]);

    const lastStep = stepIds[stepIds.length - 1];
    const firstStep = stepIds[0];
    const stepsLength = stepIds.length;

    const wizardDerivedState = React.useMemo<IWizardDerivedState>(() => ({
        currentStep: currentStepId,
        stepsLength,
        firstStep,
        lastStep,
        steps: stepIds,
        navigationStatus: navigation.status,
        stepsData,
        stepsState: steps
    }), [currentStepId, firstStep, lastStep, stepsLength, stepIds, navigation.status, stepsData, steps]);

    currentDerivedState.current = wizardDerivedState;

    const selectors = React.useMemo<IWizardStateSelectors>(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const state = () => currentDerivedState.current!;
        return {
            hasStep: (stepId) => stepIds.includes(stepId),
            isActive: (stepId) => stepId === state().currentStep,
            isFirst: (stepId) => stepId === state().firstStep,
            isLast: (stepId) => stepId === state().lastStep,
            nextStepId: (stepId) => {
                const currState = state();
                return currState.steps[currState.steps.indexOf(stepId) + 1];
            },
            previousStepId: (stepId) => {
                const currState = state();
                return currState.steps[currState.steps.indexOf(stepId) - 1];
            },
            isStepInRange: (stepId) => {
                const currState = state();
                const stepIndex = currState.steps.indexOf(stepId);
                return stepIndex >= 0 && stepIndex < currState.stepsLength;
            },
            isBetween: (stepId) => state().steps.includes(stepId) &&
                stepId !== state().firstStep
                && stepId !== state().lastStep,
            getStepsData: () => state().stepsData,
            getStepParams: (stepId) => state().stepsState[stepId]?.params,
            getStepData: (stepId) => state().stepsData[stepId],
            hasVisited: (stepId) => state().stepsState[stepId]?.visited === true
        };
    }, [stepIds]);

    const actions = React.useMemo<IWizardActions>(() => ({
        addStep, removeStep,
        navigate, saveStepData, clearAllStepsData
    }), [addStep, removeStep, navigate, saveStepData, clearAllStepsData]);

    React.useEffect(() => {
        if (defaultActiveStep) {
            onStepActivated({ currentStep: currentStepId, from: lastActiveStepId.current });
            lastActiveStepId.current = currentStepId;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepId, onStepActivated]);

    return React.useMemo<IWizardInstance>(() => ({
        actions, state: wizardDerivedState,
        selectors, eventManager, on: eventsProxy
    }), [actions, wizardDerivedState, selectors, eventManager, eventsProxy]);

};
